import arrayUtils from "@/common/utils/arrayUtils";
import { settleAfterRotate } from "@/modules/rotateSettle";
import type { Size } from "@/common/utils/dimensionUtils";

export type SizeListener = (size: Size) => void;

const listeners: SizeListener[] = [];
let monitoring = false;
let cancelOrientationSettle: (() => void) | null = null;
let size: Size = measureWindow();

function measureWindow(): Size {
  return {
    width:
      window.innerWidth ||
      document.documentElement.clientWidth ||
      document.body.clientWidth,
    height:
      window.innerHeight ||
      document.documentElement.clientHeight ||
      document.body.clientHeight,
  };
}

export function monitorWindow(value: boolean) {
  if (value) {
    if (!monitoring) {
      window.addEventListener("resize", onWindowResize);
      window.addEventListener("orientationchange", onOrientationChange);
      monitoring = true;
    }
  } else if (monitoring) {
    window.removeEventListener("resize", onWindowResize);
    window.removeEventListener("orientationchange", onOrientationChange);
    if (cancelOrientationSettle) {
      cancelOrientationSettle();
      cancelOrientationSettle = null;
    }
    monitoring = false;
  }
}

export function addListener(listener: SizeListener) {
  listeners.push(listener);
}

export function removeListener(listener: SizeListener) {
  arrayUtils.remove(listeners, listener);
}

export function getSize() {
  return size;
}

export function setSize(next: Size) {
  if (next.width !== size.width || next.height !== size.height) {
    size = next;
    notifyListeners();
  }
}

function notifyListeners() {
  for (let n = 0, max = listeners.length; n < max; n++) {
    listeners[n](size);
  }
}

function onWindowResize() {
  setSize(measureWindow());
}

// On iOS the rotate fires `resize` with an INTERMEDIATE viewport (so a
// portrait video can end up sized to a landscape-ish width, leaving a right-side
// gap) and then no final `resize` once the viewport settles — so `size` stays
// stale. `orientationchange` is the reliable rotate signal: re-measure every
// frame via settleAfterRotate until the viewport settles (as fast as possible).
// `setSize` only notifies on a real change, so the repeats are harmless no-ops.
function onOrientationChange() {
  if (cancelOrientationSettle) {
    cancelOrientationSettle();
  }
  cancelOrientationSettle = settleAfterRotate(function () {
    setSize(measureWindow());
  });
}

monitorWindow(true);
